import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { addYears, format, isBefore } from 'date-fns';
import React, { useState } from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import StarsDatePicker from '@swf/components/Inputs/StarsDatePicker';
import { select } from '@swf/core/store';
import { equalOrBeforeDate, required } from '@swf/core/utils/validators';

import useDisableField from '../../../../../../hooks/useDisableField';
import { RmfDocumentType, docTypeField } from '../DocumentTypeField/DocumentTypeField';
import { expiryDateField } from '../ExpiryDateField/ExpiryDateField';

import {
  TXTCLI_Invalid_date,
  TXTWCL_Issue_date,
  TXTWCL_The_date_your_document_was_iss_ELL
} from './IssueDateField.messages.json';
import { container, disable, issueSelect, label, wrapper } from './IssueDateField.scss';

interface FlatPickerElement extends HTMLInputElement {
  _flatpickr: {
    setDate(
      date: string | Date | Date[],
      triggerChangeEvent?: boolean,
      dateStrFormat?: string
    ): void;
  };
}

const issueDateField = 'docIssueDate';
const dateFormat = 'yyyy-LL-dd';

const IssueDateField: React.FC = () => {
  const [wasAutomaticExpirySet, setWasAutomaticExpirySet] = useState(false);
  const dob = useSelector(select.account.getDateOfBirth);
  const disableField = useDisableField();
  const {
    input: { value: issueDate },
    meta: { valid: issueDateValid }
  } = useField(issueDateField, { subscription: { value: true, valid: true } });

  const {
    input: { value: docType }
  } = useField(docTypeField, { subscription: { value: true } });

  const getExpiryDate = () => {
    if (docType === RmfDocumentType.DriversLisence) {
      const fiftyYrsDate = dob && addYears(new Date(dob), 50);
      const docExpiryDate = addYears(
        new Date(issueDate as string),
        isBefore(fiftyYrsDate as Date, new Date(issueDate)) ? 5 : 10
      );

      return docExpiryDate;
    }

    return addYears(new Date(issueDate as string), 10);
  };

  const handleValueUpdate = () => {
    if (!issueDate || !issueDateValid || wasAutomaticExpirySet) return;

    const expiryDate = getExpiryDate();
    const datePicker = document.getElementById(expiryDateField) as FlatPickerElement;
    // eslint-disable-next-line no-underscore-dangle
    datePicker?._flatpickr?.setDate?.(expiryDate as Date, true, dateFormat);
    setWasAutomaticExpirySet(true);
  };

  return (
    <div className={wrapper}>
      <StarsDatePicker
        id={issueDateField}
        label={<Translation {...TXTWCL_Issue_date} />}
        validators={[
          required(TXTWCL_The_date_your_document_was_iss_ELL),
          equalOrBeforeDate(format(new Date(), dateFormat), TXTCLI_Invalid_date)
        ]}
        hideDateFormat
        onValueUpdate={handleValueUpdate}
        className={classNames(issueSelect, {
          [disable]: disableField(issueDateField)
        })}
        customContainerClassName={container}
        labelClassName={label}
      />
    </div>
  );
};

export default IssueDateField;
